import { BaseEntity, type EntitySaveOptions, type ValidationResult } from '@memberjunction/core';
import { RegisterClass } from '@memberjunction/global';
import {
    FeedbackPlatformConversationMessageEntity,
    FeedbackPlatformConversation__FeedbackPlatformEntity,
    FeedbackPlatformReviewThankYouNoteEntity,
} from 'mj_generatedentities';
import { DecideConversationCreate, DecideOwnedBySaver, DecideThankYou, DecideThreadMessage } from '../rules/decisions.js';
import { CreateOEPRuleData } from '../rules/data.js';
import { OEP_ENTITY } from '../contracts/entities.js';
import { Apply, BeginRules, TrustedID } from './shared.js';

@RegisterClass(BaseEntity, OEP_ENTITY.ConversationMessages)
export class OEPConversationMessageEntity extends FeedbackPlatformConversationMessageEntity {
    override get DefaultSkipAsyncValidation(): boolean { return false; }

    /** Computes ThreadPosition server-authoritatively; the unique index turns a race into a failed insert. */
    override async Save(options?: EntitySaveOptions): Promise<boolean> {
        if (!this.IsSaved) {
            const data = CreateOEPRuleData(this.RunViewProviderToUse, this.ActiveUser);
            const review = await data.ReviewForConversation(this.ConversationID);
            const ctx = review ? await data.PresentationForReview(review.ReviewID) : null;
            if (ctx?.SeasonID) this.ThreadPosition = (await data.ThreadSenders(this.ConversationID)).length + 1;
        }
        return super.Save(options);
    }

    override async ValidateAsync(): Promise<ValidationResult> {
        const result = await super.ValidateAsync();
        const rc = await BeginRules(this, this.ActiveUser, result, 'ConversationMessages', this.ID, {
            ConversationID: this.ConversationID, SenderUserID: this.SenderUserID, ThreadPosition: this.ThreadPosition,
        });
        // Updates (v1 marks-read: ReadDate) only need the immutable-column guard above.
        if (!rc || this.IsSaved) return result;
        // Applies to every thread, archive included: nobody posts as someone else.
        Apply(result, DecideOwnedBySaver({ IsStaffOrSystem: rc.IsStaff, Field: 'SenderUserID', OwnerUserID: this.SenderUserID, SaverUserID: rc.SaverUserID }), this.SenderUserID);
        const review = await rc.Data.ReviewForConversation(this.ConversationID);
        const ctx = review ? await rc.Data.PresentationForReview(review.ReviewID) : null;
        if (!ctx || !ctx.SeasonID || !ctx.Phase) return result; // archive thread
        Apply(result, DecideThreadMessage({
            Phase: ctx.Phase, ReviewStatus: ctx.ReviewStatus, PresenterUserID: ctx.PresenterUserID,
            Existing: await rc.Data.ThreadSenders(this.ConversationID), SenderUserID: this.SenderUserID,
        }), this.ConversationID);
        return result;
    }
}

@RegisterClass(BaseEntity, OEP_ENTITY.Conversations)
export class OEPConversationEntity extends FeedbackPlatformConversation__FeedbackPlatformEntity {
    override get DefaultSkipAsyncValidation(): boolean { return false; }
    override async ValidateAsync(): Promise<ValidationResult> {
        const result = await super.ValidateAsync();
        const rc = await BeginRules(this, this.ActiveUser, result, 'Conversations', this.ID, { ReviewID: this.ReviewID });
        if (!rc || this.IsSaved) return result; // updates (LastMessageDate, Subject) keep only the FK guard
        const ctx = await rc.Data.PresentationForReview(this.ReviewID);
        if (!ctx || !ctx.SeasonID || !ctx.Phase) return result;
        Apply(result, DecideConversationCreate({ Phase: ctx.Phase, ReviewStatus: ctx.ReviewStatus, ExistingCount: await rc.Data.ConversationCountForReview(this.ReviewID) }), this.ReviewID);
        return result;
    }
}

@RegisterClass(BaseEntity, OEP_ENTITY.ReviewThankYouNotes)
export class OEPReviewThankYouNoteEntity extends FeedbackPlatformReviewThankYouNoteEntity {
    override get DefaultSkipAsyncValidation(): boolean { return false; }
    override async ValidateAsync(): Promise<ValidationResult> {
        const result = await super.ValidateAsync();
        const rc = await BeginRules(this, this.ActiveUser, result, 'ReviewThankYouNotes', this.ID, { ReviewID: this.ReviewID });
        if (!rc) return result;
        const ctx = await rc.Data.PresentationForReview(TrustedID(rc, 'ReviewID', this.ReviewID));
        if (!ctx || !ctx.Phase) return result;
        Apply(result, DecideThankYou({ Phase: ctx.Phase, ReviewStatus: ctx.ReviewStatus }), this.ReviewID);
        return result;
    }
}
